import React from "react";
// Import all standardized components from EmailModelComponents
import LeftAlignImage from "../../components/Email/EmailModelComponents/LeftAlignImage";
import RightAlignImage from "../../components/Email/EmailModelComponents/RightAlignImage";
import ThreeIconContent from "../../components/Email/EmailModelComponents/ThreeIconContent";
import Banner from "../../components/Email/EmailModelComponents/Banner";
import TitleDescription from "../../components/Email/EmailModelComponents/TitleDescription";
import ProductGrid from "../../components/Email/EmailModelComponents/ProductGrid";
import ImageTextFeature from "../../components/Email/EmailModelComponents/ImageTextFeature";
import HeaderImageText from "../../components/Email/EmailModelComponents/HeaderImageText";
import HeadingDescription from "../../components/Email/EmailModelComponents/HeadingDescription";
import CenteredHeading from "../../components/Email/EmailModelComponents/CenteredHeading";
import OfferBlock from "../../components/Email/EmailModelComponents/OfferBlock";
import MultiColumnLayout from "../../components/Email/EmailModelComponents/MultiColumnLayout";
import Header from "../../components/Email/EmailModelComponents/Header";
import Footer from "../../components/Email/EmailModelComponents/Footer";
import Button from "../../components/Email/EmailModelComponents/Button";
import LeftImageRightContent from "../../components/Email/EmailModelComponents/LeftImageRightContent";
import RightImageLeftContent from "../../components/Email/EmailModelComponents/RightImageLeftContent";
import ImgCircle from "../../components/Email/EmailModelComponents/Img_circle";
import TwoColThreeRowsBenefits from "../../components/Email/EmailModelComponents/2COL_3ROWS_Benefits";
import DescriptionBtn from "../../components/Email/EmailModelComponents/Description_btn";
import OfferDetailsH1Desc from "../../components/Email/EmailModelComponents/OfferDetails_H1_Desc";
import Title_4Img from "../../components/Email/EmailModelComponents/Title_4Img";

// Create a wrapper to properly configure components for preview mode
const PreviewComponent = ({ Component, data, styleSettings }) => {
  // Handle image paths for preview - ensure URLs are properly formatted
  const processImageData = (data) => {
    // Create a copy of the data to modify
    const processedData = { ...data };
    
    // Helper function to clean image URLs
    const cleanImageUrl = (url) => {
      if (!url) return url;
      if (typeof url !== 'string') return url;
      
      // Remove any timestamp parameters that might cause caching issues
      if (url.includes('&t=')) {
        return url.split('&t=')[0];
      }
      return url;
    };
    
    // Process image field if it exists
    if (processedData.image) {
      processedData.image = cleanImageUrl(processedData.image);
    }
    
    // Process other image fields that might exist
    ['image1', 'image2', 'image3', 'headerImage', 'backgroundImage'].forEach(field => {
      if (processedData[field]) {
        processedData[field] = cleanImageUrl(processedData[field]);
      }
    });
    
    // IMPORTANT: Always force previewMode to true for all preview components
    processedData.previewMode = true;
    
    // Special handling for the Footer component
    if (processedData.component_name === 'Footer' && !processedData.links) {
      processedData.links = [];
    }
    
    // Special handling for TitleDescription components
    if (processedData.component_name === 'TitleDescription' && !processedData.features) {
      processedData.features = [];
    }
    
    // Ensure features exist for components that need them
    if (['Left_Image_Right_Title_Desc_Btn', 'Right_Image_Left_Title_Desc_Btn'].includes(processedData.component_name) && !processedData.features) {
      processedData.features = [];
    }
    
    return processedData;
  };
  
  // Process the data for preview
  const previewData = processImageData(data);
  
  // Log component rendering in preview mode
  console.log(`Rendering ${previewData.component_name} in preview mode`, previewData);
  
  // Stub update function that does nothing - prevents any editing in preview
  const stubOnUpdate = () => {
    console.log("Edit operations are disabled in preview mode");
  };
  
  return (
    <Component
      id={data.id || `preview-${Math.random().toString(36).substring(2, 9)}`}
      data={previewData}
      onUpdate={stubOnUpdate}
      styleSettings={styleSettings}
      previewMode={true} // Explicitly force previewMode to true
    />
  );
};

// Component mapping with direct references to actual components
const COMPONENT_MAP = {
  // Core components
  Header: Header,
  Banner: Banner,
  Img_circle: ImgCircle,
  Img_Circle: ImgCircle,
  Description_btn: DescriptionBtn,
  Description_Btn: DescriptionBtn,
  Centre_btn: DescriptionBtn,
  Center_btn: DescriptionBtn,
  "2COL_3ROWS_Benefits": TwoColThreeRowsBenefits,
  "2Col_3Rows_Benefits": TwoColThreeRowsBenefits,
  Footer: Footer,
  Button: Button,
  
  // Two column layouts
  "2Col_1Img_left": RightAlignImage,  // Match DynamicComponent.jsx mapping
  "2Col_1Img_right": LeftAlignImage,  // Match DynamicComponent.jsx mapping
  
  // Multi-column layouts
  "3Col_3Img": ThreeIconContent,
  "ThreeCol_1Imgleft": props => <MultiColumnLayout {...props} columns={3} />,
  "FourCol_1Imgleft": props => <MultiColumnLayout {...props} columns={4} />,
  "4Col": Title_4Img,
  "4Col_4Img": Title_4Img,
  "Title_4Img": Title_4Img,
  "H1_4Img": Title_4Img,
  
  // Title and descriptions
  "Title_Desc": TitleDescription,
  "Description": TitleDescription,
  "TitleDescription": TitleDescription,
  
  // Product components
  "productGrid": ProductGrid,
  "2Col_1Img_left_H1_H2": ImageTextFeature,
  "Header_2Col_1Img_Left": HeaderImageText,
  
  // Heading variations
  "H1_H2_Desc": TitleDescription,
  "H1_H2_Desc_Code": OfferBlock,
  "H1_H2_Desc_4": HeadingDescription,
  "H1_H2_Desc_9": HeadingDescription,
  "H1_Desc_4": HeadingDescription,
  "H1_Desc_5": HeadingDescription,
  "H1_Desc_6": HeadingDescription,
  "H1_CenterAlign_Desc": CenteredHeading,
  "Header_Title_Desc": HeadingDescription,
  
  // Offer components
  "OfferDetails_H1_Code": OfferBlock,
  "OfferDetails_H1_Desc": OfferDetailsH1Desc,
  
  // Legacy support - Updated to use new components
  "Left_Image_Right_Title_Desc_Btn": LeftImageRightContent,
  "Right_Image_Left_Title_Desc_Btn": RightImageLeftContent,
  "Social Links": TitleDescription
};

// Add global CSS for preview mode to ensure components display properly
// Track how many components are using the styles to prevent removal issues
let styleRefCount = 0;

const applyGlobalStyling = (styleSettings) => {
  const { primaryColor, secondaryColor, fontFamily } = styleSettings;
  
  // Increment reference counter
  styleRefCount++;
  console.log(`[Style] Adding reference: ${styleRefCount}`);
  
  // Add a style tag to the document head for preview mode
  const styleId = 'email-preview-global-styles';
  let styleEl = document.getElementById(styleId);
  
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    styleEl.setAttribute('data-ref-count', styleRefCount.toString());
    document.head.appendChild(styleEl);
    console.log(`[Style] Created new style element: ${styleId}`);
  } else {
    // Update reference count
    styleEl.setAttribute('data-ref-count', styleRefCount.toString());
    console.log(`[Style] Updated existing style element: ${styleId}`);
  }
  
  // Define global styles based on the current theme
  styleEl.innerHTML = `
    .product-section {
      font-family: ${fontFamily} !important;
      margin-bottom: 20px !important;
    }
    
    .email-content-container {
      font-family: ${fontFamily} !important;
      background-color: #ffffff;
    }
    
    .product-section h1, .product-section h2, .product-section h3, 
    .product-section h4, .product-section h5, .product-section h6 {
      color: ${secondaryColor} !important;
    }
    
    .product-section p, .product-section div:not(.section-header) {
      color: ${primaryColor} !important;
    }
    
    .email-header p {
      color: ${primaryColor} !important;
    }
    
    .email-header p strong {
      color: ${secondaryColor} !important;
    }
    
    /* Hide ALL editing UI elements in preview mode */
    .section-header,
    .edit-controls,
    .editing-ui,
    .edit-mode-only,
    .add-feature-button-container,
    .image-upload-label,
    .email-preview-component button:not(.btn-primary),
    .email-preview-component input,
    .email-preview-component textarea,
    .email-preview-component .draggable-handle {
      display: none !important;
    }
    
    /* Make all preview components non-editable */
    .email-preview-component {
      pointer-events: auto !important; /* Allow clicking links */
      cursor: default;
    }
    
    .email-preview-component * {
      outline: none !important;
    }
    
    /* Allow clicking on links and buttons in preview mode */
    .email-preview-component a,
    .email-preview-component .btn,
    .email-preview-component button.btn-primary {
      pointer-events: auto !important;
      cursor: pointer !important;
    }
    
    /* Remove editing borders and backgrounds */
    .email-preview-component .component-placeholder,
    .email-preview-component .image-placeholder {
      border: 1px solid #eee !important;
      background-color: #f9f9f9 !important;
    }
    
    .product-section .description-text {
      color: ${primaryColor} !important;
    }
    
    .product-section .productDescription {
      color: ${primaryColor} !important;
    }
    
    /* Fix for Bootstrap classes that might be missing in preview iframe */
    .d-flex {
      display: flex !important;
    }
    
    .justify-content-center {
      justify-content: center !important;
    }
    
    .justify-content-between {
      justify-content: space-between !important;
    }
    
    .align-items-center {
      align-items: center !important;
    }
    
    .text-center {
      text-align: center !important;
    }
    
    .position-relative {
      position: relative !important;
    }
    
    .position-absolute {
      position: absolute !important;
    }
    
    .w-100 {
      width: 100% !important;
    }
    
    .h-100 {
      height: 100% !important;
    }
    
    .img-fluid {
      max-width: 100%;
      height: auto;
    }
    
    .rounded {
      border-radius: 0.25rem !important;
    }
    
    .mb-2 {
      margin-bottom: 0.5rem !important;
    }
    
    .mb-3 {
      margin-bottom: 1rem !important;
    }
    
    .mb-4 {
      margin-bottom: 1.5rem !important;
    }
    
    .px-3 {
      padding-left: 1rem !important;
      padding-right: 1rem !important;
    }
    
    .py-2 {
      padding-top: 0.5rem !important;
      padding-bottom: 0.5rem !important;
    }
    
    .py-3 {
      padding-top: 1rem !important;
      padding-bottom: 1rem !important;
    }
  `;
};

// Cleanup global styling - only remove when no components are using it
const cleanupGlobalStyling = () => {
  // Decrement reference counter
  styleRefCount = Math.max(0, styleRefCount - 1);
  console.log(`[Style] Removing reference: ${styleRefCount}`);
  
  const styleId = 'email-preview-global-styles';
  const styleEl = document.getElementById(styleId);
  
  if (styleEl) {
    // Update reference count
    styleEl.setAttribute('data-ref-count', styleRefCount.toString());
    
    // Only remove if no components are using it
    if (styleRefCount === 0) {
      try {
        if (styleEl.parentNode) {
          styleEl.parentNode.removeChild(styleEl);
          console.log(`[Style] Removed style element: ${styleId}`);
        }
      } catch (error) {
        console.warn(`[Style] Error removing style element: ${error.message}`);
      }
    } else {
      console.log(`[Style] Keeping style element (${styleRefCount} refs remaining)`);
    }
  }
};

function DynamicEmailSectionRenderer({ section, styleSettings = {} }) {
  // Merge section-specific styleSettings with global styleSettings
  const sectionStyleSettings = section?.styleSettings || {};
  const effectiveStyleSettings = {
    ...styleSettings,  // Global styles (defaults)
    ...sectionStyleSettings  // Section-specific overrides
  };
  
  // Apply global styling (use effective styles for global CSS)
  React.useEffect(() => {
    applyGlobalStyling(effectiveStyleSettings);
    return () => {
      cleanupGlobalStyling();
    };
  }, [effectiveStyleSettings]);
  
  // Handle case where section might be null or undefined
  if (!section) {
    console.error("Empty section data provided to DynamicEmailSectionRenderer");
    return null;
  }
  
  // Get component type from the data
  const componentType = section.component_name || "";
  
  if (!componentType) {
    console.error("Missing component_name in section data:", section);
    return (
      <div style={{
        padding: "15px",
        backgroundColor: "#f8f9fa",
        border: "1px solid #dee2e6",
        borderRadius: "4px",
        color: "#6c757d",
        textAlign: "center"
      }}>
        Unknown Component
      </div>
    );
  }
  
  // Get the component from our map
  const Component = COMPONENT_MAP[componentType];
  
  // Case insensitive fallback
  let fallbackComponent = null;
  if (!Component) {
    const lowerComponentType = componentType.toLowerCase();
    const keys = Object.keys(COMPONENT_MAP);
    
    for (const key of keys) {
      if (key.toLowerCase() === lowerComponentType) {
        fallbackComponent = COMPONENT_MAP[key];
        console.log(`Using ${key} for ${componentType}`);
        break;
      }
    }
  }
  
  // Render the component if found
  if (Component || fallbackComponent) {
    const ComponentToRender = Component || fallbackComponent;
    
    return (
      <div className="email-preview-component">
        <PreviewComponent 
          Component={ComponentToRender} 
          data={section} 
          styleSettings={effectiveStyleSettings} 
        />
      </div>
    );
  }
  
  // Fallback for unknown components
  console.warn(`No component found for type: ${componentType}`);
  return (
    <div style={{
      padding: "15px",
      backgroundColor: "#f8f9fa",
      border: "1px solid #dee2e6",
      borderRadius: "4px",
      color: "#6c757d",
      textAlign: "center"
    }}>
      <div>Unknown component type: {componentType}</div>
      <pre style={{ fontSize: "11px", textAlign: "left", marginTop: "10px" }}>
        {JSON.stringify(section, null, 2)}
      </pre>
    </div>
  );
}

export default DynamicEmailSectionRenderer;
